<template>
  <div
    :id="id"
    class="tinymce"
  >
    <slot />

    <label v-if="label">
      {{ label }}
    </label>

    <Editor
      :model-value="model"
      class="editor-root"
      :init="editorConfig"
      @update:model-value="handleContentUpdate"
    />

    <!-- Word Count Display -->
    <small>
      Recommended word count: {{ recommendedWordCount }} words
    </small>

    <!--    <small class="pl-1 flex justify-between items-center">-->
    <!--      &lt;!&ndash; Word Count Display &ndash;&gt;-->
    <!--      <span-->
    <!--        v-if="wordLimit > 0 && currentWordCount > (wordLimit - 50)"-->
    <!--        class="word-limit text-red-600"-->
    <!--      >-->
    <!--        {{ currentWordCount }} / {{ wordLimit }} words-->
    <!--      </span>-->
    <!--    </small>-->

    <span
      v-if="validationError"
      class="text-red-600 text-xs"
    >
      {{ validationError }}
    </span>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import axios, { HttpStatusCode } from 'axios';

import 'tinymce';

import 'tinymce/icons/default/icons.min.js';

/* Required TinyMCE components */
import 'tinymce/themes/silver/theme.min.js';
import 'tinymce/models/dom/model.min.js';

/* Import a skin (can be a custom skin instead of the default) */
import 'tinymce/skins/ui/oxide/skin.js';

import 'tinymce/icons/default';
import 'tinymce/models/dom/model';
// import 'tinymce/skins/content/default/content.css';
// import 'tinymce/skins/ui/oxide/content.css';
// import 'tinymce/skins/ui/oxide/skin.min.css';
// import 'tinymce/skins/ui/oxide/content.min.css';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/image';
import 'tinymce/plugins/code';
import 'tinymce/themes/silver';
import 'tinymce/plugins/wordcount';

/* content UI CSS is required */
import 'tinymce/skins/ui/oxide/content.js';

/* The default content CSS can be changed or replaced with appropriate CSS for the editor content. */
import 'tinymce/skins/content/default/content.js';

import Editor from '@tinymce/tinymce-vue';

import { useDomPurify, useToasts } from '@/composables/index.js';
import { usePlanStore } from '@/store/index.js';

const model = defineModel();
const planStore = usePlanStore();
const domPurify = useDomPurify();
const toasts = useToasts();

const emit = defineEmits(['update:wordCount', 'update:captions']);

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, default: '' },
  inline: { type: Boolean, default: false },
  validationError: { type: String, default: null },
  placeholder: { type: String, default: null },
  height: { type: Number, default: 450 },
  recommendedWordCount: { type: String, default: null },
  wordLimit: { type: Number, default: 0 },
  totalWordCount: { type: Number, default: 0 },
  allowImageUpload: { type: Boolean, default: true },
});

const isReady = ref(false);
const currentWordCount = ref(0);

const plugins = ['link', 'autolink', 'lists', 'wordcount'];
let toolbar = 'blocks bold italic bullist numlist link code';

if (props.allowImageUpload) {
  plugins.push('image');
  toolbar += ' image';
}

const captionPlaceholderText = 'Insert figure name here';
const captionPlaceholderClass = 'placeholder-caption';

// scss cant get passed into the tinymce iframe so we use the tinymce css handler
const css = `
  @font-face { font-family: 'Inter'; font-weight: 300; src: url('/fonts/inter/inter-v13-latin-300.woff2') format('woff2'); }
  @font-face { font-family: 'Inter'; font-weight: 400; src: url('/fonts/inter/inter-v13-latin-regular.woff2') format('woff2'); }
  @font-face { font-family: 'Inter'; font-weight: 500; src: url('/fonts/inter/inter-v13-latin-500.woff2') format('woff2'); }
  @font-face { font-family: 'Inter'; font-weight: 600; src: url('/fonts/inter/inter-v13-latin-600.woff2') format('woff2'); }
  @font-face { font-family: 'Inter'; font-weight: 700; src: url('/fonts/inter/inter-v13-latin-700.woff2') format('woff2'); }

  body { font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.8; }

  /* Placeholders */
  .mce-content-body[data-mce-placeholder]::before { content: attr(data-mce-placeholder); position: absolute; cursor: text; color: #9ca3af; }

  /* Headings */
  h3 { font-weight: 600; color: black; font-size: 19px; margin: 0; }
  h4, h5 { font-weight: 600; color: black; font-size: 15px; margin: 0; }
  p { margin: 0; }
  ul { list-style: disc; padding-left: 1.5em; }
  ol { list-style: decimal; padding-left: 1.5em; }
  li { margin: 0.25em 0; }

  [data-mce-caret="before"] {
    display: none;
  }
  figure.image {
    display: inline-block;
    width: 100%;
    margin: 0 0 1em 0; /* mb-[1em] */
  }

  figure.image img {
    display: block;
    width: 100%;
    max-width: 100%;
    height: auto;
  }

  figure.image figcaption {
    transition: none;
    display: block;
    width: 100%;
    border: 1px solid #E2E1E3; /* border-gray-400 */
    border-radius: 0.125rem;   /* rounded-sm */
    margin-top: 0.75rem;       /* mt-3 */
    padding: 0.25rem 0.5rem;           /* p-2 */
    min-height: 40px;
    font-size: 1rem;           /* text-base */
    outline: none;
    box-sizing: border-box;
    text-align: start;
    color: #111;
  }

  figure.image figcaption.placeholder-caption {
    color: #9ca3af; /* text-gray-400 */
  }

  .mce-visual-caret {
    height: 0 !important;
  }
`;

// Image Upload
const handleImageUpload = (blobInfo, progress) => new Promise(async resolve => {
  const formData = new FormData();
  formData.append('image', blobInfo.blob());

  const response = await axios({
    onUploadProgress: event => progress((event.loaded / event.total) * 100),
    data: formData,
    url: '/images',
    method: 'POST',
  });

  if (response.status === HttpStatusCode.Ok) {
    resolve(response.data.data.url);
  }
});

const handleContentUpdate = (newValue) => {
  // If we are still initializing (running auto-captions/placeholders),
  // DO NOT bubble this change up to the parent/store.
  if (isReady.value) {
    model.value = newValue;
  }
};

const editorConfig = computed(() => ({
  height: props.height,
  menubar: false,
  inline: props.inline,
  toolbar_mode: 'sliding',
  fixed_toolbar_container: '#tinymce-toolbar',
  placeholder: props.placeholder,
  image_description: false,
  image_dimensions: false,
  image_caption: false,
  convert_urls: false,
  relative_urls: false,
  remove_script_host: false,
  apiKey: false,
  license_key: 'gpl',
  plugins: plugins,
  file_picker_types: 'image',
  toolbar: toolbar,
  theme: 'silver',
  paste_data_images: props.allowImageUpload,
  paste_as_text: true,
  paste_block_drop: true,
  paste_merge_formats: true,
  smart_paste: true,
  branding: false,
  images_upload_handler: handleImageUpload,
  elementpath: false,
  block_formats: 'Paragraph=p; Heading 1=h3; Heading 2=h4; Heading 3=h5;',
  paste_preprocess: function(editor, args) {
    let content = args.content;

    if (!props.allowImageUpload) {
      content = content.replace(/<img[^>]*>/gi, '');
    }
    args.content = domPurify.sanitize(content);

    // Prevent massive pastes if over limit
    if (props.wordLimit > 0) {
      // Simple word count check for pasted text
      const pastedWordCount = args.content.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
      if (props.totalWordCount + pastedWordCount > props.wordLimit) {
        args.content = ''; // Block the paste
        toasts.error('Word limit reached. Please remove some words before pasting.');
      }
    }
  },
  content_style: css,
  setup: (editor) => {
    const cleanupEmptyFigures = () => {
      // Find all figures
      const figures = editor.dom.select('figure');
      figures.forEach(figure => {
        // Check if the figure contains an IMG tag
        const img = editor.dom.select('img', figure);

        // If there is NO image inside the figure, remove the entire figure
        if (img.length === 0) {
          editor.dom.remove(figure);
        }
      });
    };

    // wrap images in a <figure>
    const autoCaptionImages = () => {
      let changed = false;

      const images = editor.dom.select('img');
      images.forEach(img => {
        // If image is already inside a figure, skip it
        if (editor.dom.getParent(img, 'figure')) return;

        // If the image is inside a P tag, we want to replace the P tag,
        // not just the image. This prevents invalid nesting (P > Figure).
        const parent = img.parentNode;
        let targetToReplace = img;

        // Check if parent is a <p> and seemingly only contains this image/whitespace
        if (parent && parent.nodeName === 'P' && parent.innerText.trim() === '') {
          targetToReplace = parent;
        }

        // Create the figure wrapper
        const figure = editor.dom.create('figure', { class: 'image' });
        const caption = editor.dom.create(
          'figcaption',
          { class: captionPlaceholderClass },
          captionPlaceholderText,
        );

        // Build the figure
        const newImg = img.cloneNode(true);
        figure.appendChild(newImg);
        figure.appendChild(caption);

        // Replace the TARGET (either the img or its parent P)
        editor.dom.replace(figure, targetToReplace);

        // Insert empty paragraph AFTER the figure
        // Since we replaced the parent P, this now sits cleanly in the root flow
        const emptyParagraph = editor.dom.create('p', null, '<br>');
        editor.dom.insertAfter(emptyParagraph, figure);

        changed = true;
      });

      return changed;
    };

    // Scrape the DOM to update the store
    const extractAndSyncCaptions = () => {
      if (!isReady.value) return;

      const figures = editor.dom.select('figure');

      const myFigures = figures.map(figure => {
        const img = figure.querySelector('img');
        const figcaption = figure.querySelector('figcaption');

        if (img && figcaption) {
          const text = figcaption.innerText.trim();

          // Check if text is valid (not empty, not the placeholder)
          const isValidCaption = text !== '' && text !== captionPlaceholderText;

          return {
            editorId: props.id,
            src: img.getAttribute('src'),
            caption: isValidCaption ? text : '',
          };
        }
        return null;
      }).filter(item => item !== null && item.src);

      const otherFigures = (planStore.figures || []).filter(
        f => f.editorId !== props.id,
      );

      planStore.figures = [...otherFigures, ...myFigures];
    };

    // create placeholders for init
    const initPlaceholderCaptions = () => {
      const captions = editor.dom.select('figcaption');
      captions.forEach(caption => {
        const text = caption.innerText.trim();

        if (text === '') {
          caption.innerText = captionPlaceholderText;
          editor.dom.addClass(caption, captionPlaceholderClass);
        }
      });
    };

    const restorePlaceholders = (forceRestoreAll = false) => {
      // If we aren't forcing all (e.g. on blur), find the caption we are currently editing so we don't reset it
      const currentCaption = forceRestoreAll ? null : editor.dom.getParent(editor.selection.getNode(), 'figcaption');
      const allCaptions = editor.dom.select('figcaption');

      allCaptions.forEach(caption => {
        // Skip the caption currently being edited
        if (currentCaption === caption) return;

        // Check clean text content
        const text = caption.textContent.replace(/[\u200B\u00A0]/g, '').trim();

        if (text === '') {
          // Only apply if it's not already a placeholder (prevents loops)
          if (caption.innerText !== captionPlaceholderText) {
            editor.dom.addClass(caption, captionPlaceholderClass);
            caption.innerText = captionPlaceholderText;
          }
        }
      });
    };

    const updateWordCount = () => {
      if (!isReady.value) return;

      const count = editor.plugins.wordcount.body.getWordCount();
      currentWordCount.value = count;
      emit('update:wordCount', { id: props.id, count: count });
    };

    // editor event listeners
    editor.on('init', () => {
      updateWordCount();

      if (props.allowImageUpload) {
        initPlaceholderCaptions();
      }

      setTimeout(() => {
        isReady.value = true;
      }, 100);
    });

    editor.on('keydown', (e) => {
      // Always allow navigation and deletion
      const allowedKeys = [8, 46, 37, 38, 39, 40];
      if (allowedKeys.includes(e.keyCode) || e.ctrlKey || e.metaKey) return;

      const isOverLimit = props.totalWordCount > props.wordLimit;
      const isAtLimit = props.totalWordCount === props.wordLimit;

      // Already over the limit? Block everything.
      if (props.wordLimit > 0 && isOverLimit) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Exactly AT the limit?
      // Allow letters , but BLOCK new word separators (Space/Enter).
      if (props.wordLimit > 0 && isAtLimit) {
        const isSeparator = (e.key === ' ' || e.keyCode === 32 || e.keyCode === 13);
        if (isSeparator) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    });

    editor.on('input change undo redo setcontent', () => {
      if (props.allowImageUpload) {
        // Run cleanup first
        cleanupEmptyFigures();

        // Then handle auto-captioning for new images (e.g. paste)
        autoCaptionImages();

        // Finally sync
        extractAndSyncCaptions();

        updateWordCount();
      }
    });

    // auto select Upload tab in image modals
    editor.on('OpenWindow', () => {
      setTimeout(() => {
        // Find tab by text "Upload"
        const tabs = document.querySelectorAll('.tox-dialog__body-nav-item');
        const uploadTab = Array.from(tabs).find(t => t.innerText.trim() === 'Upload');
        uploadTab?.click();
      }, 50);
    });

    // Handle clicks on placeholder
    editor.on('click', (e) => {
      const target = e.target;
      const caption = editor.dom.getParent(target, 'figcaption');
      if (caption) {
        if (caption.innerText.trim() === captionPlaceholderText) {
          caption.innerText = '';
          editor.dom.removeClass(caption, captionPlaceholderClass);
          editor.selection.select(caption);
          editor.selection.setCursorLocation(caption, caption.childNodes.length);
          editor.focus();
        }
      }
    });

    editor.on('nodechange', () => {
      restorePlaceholders(false);
      extractAndSyncCaptions();
    });

    // Fires when clicking OUTSIDE the editor entirely
    editor.on('focusout', () => {
      restorePlaceholders(true); // Force check on all captions since none are active
      extractAndSyncCaptions();
    });
  },
}));
</script>
